import { Injectable } from '@angular/core';
import { LoggerService } from '../../services/logger.service';
import { TemplatePopulatorService } from './template-populator.service';
import { WebsiteElement } from '@way/website-themes/themes/minimal_website/home-page-preview/home-page-preview.component';
import { HeroPanelData } from '../components/template/panels/hero-panel';
import { InfoPanelData } from '../components/template/panels/info-panel/info-panel.component';
import { ContactData } from '../components/template/panels/contact-panel/contact-panel.component';
import {
  PanelButtonNavigation,
  buttonNavigationToHashLink,
  linkToButtonNavigation,
} from '../shared/panel-button-navigation';
import { mapContactBusinessHours } from '../../shared/utils/map-contact-business-hours.util';
import { resolveFooterQuickLinks } from '../../shared/utils/footer-quick-links.util';

/**
 * Mutable context bag for element selection operations.
 */
export interface ElementContext {
  elements: WebsiteElement[];
  json: any;
  templateData: any;
  website: any;
  pages: any[];
  heroPanelData: HeroPanelData;
  infoPanelData: InfoPanelData;
  bookingData: any;
  contactData: ContactData;
  testimonialsData: any;
  footerData: any;
  headerData: any;
  currentInfoSectionKey: string;
  showHeroPanel: boolean;
  showInfoPanel: boolean;
  showTestimonialsPanel: boolean;
  showBookingPanel: boolean;
  showContactPanel: boolean;
  showFooterPanel: boolean;
  showHeaderPanel: boolean;
  showWashPackagesPanel: boolean;
  showExpressDetailsPanel: boolean;
  showCouponsPanel: boolean;
  showFleetsPanel: boolean;
  loading: boolean;
  closeAllPanels(): void;
  loadWashPackagesData(): void;
  loadExpressDetailsData(): void;
  loadCouponsData(): void;
  loadFleetsData(): void;
}

/**
 * Handles website element selection, routing click events to the
 * appropriate panel (hero, info, wash packages, etc.).
 */
@Injectable({ providedIn: 'root' })
export class ElementSelectionService {
  constructor(
    private logger: LoggerService,
    private templatePopulator: TemplatePopulatorService
  ) {}
  onWebsiteElementSelect(element: any, ctx: ElementContext): void {
    // Prevent automatic selection/panel opening during initialization
    // Only allow selection if it's a user-initiated action (not during data loading)
    if (ctx.loading) {
      return;
    }

    this.updateElementSelection(element, ctx);
    this.handleElementSelection(element, ctx);
    this.setEditorDataFromElement(element, ctx);
  }

  updateElementSelection(selectedElement: any, ctx: ElementContext): void {
    ctx.elements.forEach(e => (e.selected = e.id === selectedElement.id));
  }

  handleElementSelection(element: any, ctx: ElementContext): void {
    const currentPage = ctx.pages.find((p: any) => p.selected);

    // Allow header and footer editing on all pages (they are shared components)
    if (
      element.type === 'header' ||
      element.type === 'footer' ||
      element.id === 'header' ||
      element.id === 'footer'
    ) {
      // Continue to handle header/footer regardless of page type
    } else if (element.type === 'packages') {
      // Allow packages editing on Express Wash Packages page
      const isExpressWashPackagesPage =
        currentPage &&
        ((currentPage as any).pageType === 'express-wash-packages' ||
          (currentPage as any).name?.toLowerCase().includes('express') ||
          (currentPage as any).name?.toLowerCase().includes('wash'));
      if (!isExpressWashPackagesPage) {
        this.logger.debug('Packages editing only available on Express Wash Packages page');
        return;
      }
    } else if (element.type === 'express-details') {
      // Allow express-details editing on Express Auto Details page
      const isExpressAutoDetailsPage =
        currentPage &&
        ((currentPage as any).pageType === 'express-auto-details' ||
          (currentPage as any).pageType === 'express-details' ||
          ((currentPage as any).name?.toLowerCase().includes('express') &&
            (currentPage as any).name?.toLowerCase().includes('details')));
      if (!isExpressAutoDetailsPage) {
        this.logger.debug('Express details editing only available on Express Auto Details page');
        return;
      }
    } else if (element.type === 'coupons') {
      // Allow coupons editing on Coupons page
      const isCouponsPage =
        currentPage &&
        ((currentPage as any).pageType === 'coupons' ||
          (currentPage as any).name?.toLowerCase().includes('coupon'));
      if (!isCouponsPage) {
        this.logger.debug('Coupons editing only available on Coupons page');
        return;
      }
    } else if (element.type === 'fleets') {
      // Allow fleets editing on Fleet Management page
      const isFleetsPage =
        currentPage &&
        ((currentPage as any).pageType === 'fleet-management' ||
          (currentPage as any).name?.toLowerCase().includes('fleet'));
      if (!isFleetsPage) {
        this.logger.debug('Fleets editing only available on Fleet Management page');
        return;
      }
    } else {
      // Only allow editing other elements for Home page, or 'packages'/'express-details'/'coupons'/'fleets' type for their respective pages
      if (
        !currentPage ||
        (!(currentPage as any).isHomePage &&
          element.type !== 'packages' &&
          element.type !== 'express-details' &&
          element.type !== 'coupons' &&
          element.type !== 'fleets')
      ) {
        this.logger.debug('Editing not available for this page type yet');
        return;
      }
    }

    if (element.type === 'header' || element.id === 'header') {
      ctx.closeAllPanels();
      ctx.showHeaderPanel = true;
      // Populate header data from element
      const content: any = element.content || {};

      // If navigation is empty in content, generate it from pages
      let navigation = content.navigation;
      if (!navigation || navigation.length === 0) {
        // Filter out inactive pages (visible: false means active: false)
        const activePages = ctx.pages.filter((page: any) => page.visible !== false);
        navigation = activePages.map((page: any, index: number) => ({
          id: `page_${page.pageType || index}`,
          label: page.name,
          link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
          active: index === 0,
        }));
      }

      ctx.headerData = {
        logo: content.logo || { image: '', alt: 'Logo', text: 'Logo' },
        navigation: navigation,
        ctaButton: this.buildHeaderCtaButton(content.ctaButton),
        backgroundColor: content.backgroundColor || '#F8F9FA',
      };
    } else if (element.type === 'hero') {
      // Hero section → Hero Panel
      ctx.closeAllPanels();
      ctx.showHeroPanel = true;

      this.logger.debug('🟡 handleElementSelection: hero type detected');
      this.logger.debug('🟡 element passed in:', {
        id: element.id,
        type: element.type,
        content: element.content,
      });

      // Ensure hero element is explicitly marked as selected for save operations
      const heroElement = ctx.elements.find(e => e.id === 'hero' || e.type === 'hero');
      if (heroElement) {
        ctx.elements.forEach(e => (e.selected = false));
        heroElement.selected = true;
        this.logger.debug('🟡 heroElement from websiteElements:', {
          id: heroElement.id,
          type: heroElement.type,
          content: heroElement.content,
        });
      }

      // Populate hero panel data from element
      // CRITICAL: Use heroElement content if available (it has the latest data from template)
      const content: any = heroElement?.content || element.content || {};
      this.logger.debug('🟡 content used for panel:', content);
      this.logger.debug('🟡 content.title:', content.title);
      this.logger.debug('🟡 content.description:', content.description);

      // Also check currentJsonData for hero data as another source
      const heroFromJson = ctx.json?.page?.sections?.hero_section;
      this.logger.debug(
        '🟡 heroFromJson (currentJsonData.page.sections.hero_section):',
        heroFromJson
      );

      const heading =
        this.templatePopulator.extractTitleText(content.title) ||
        this.templatePopulator.extractTitleText(heroFromJson?.title) ||
        '';
      const paragraph =
        content.description || heroFromJson?.description || heroFromJson?.subtitle || '';
      const heroTitleStyle =
        content.titleStyle ||
        this.templatePopulator.extractTitleStyleFromApi(heroFromJson?.title?.style) ||
        undefined;
      const heroImage = heroFromJson?.image || content.image;

      ctx.heroPanelData = {
        heading,
        paragraph,
        titleStyle: heroTitleStyle,
        image: {
          src: heroImage?.src || content.image?.src || content.imageUrl || '',
          alt: heroImage?.alt || content.image?.alt || '',
          width: heroImage?.width || content.image?.width || '420px',
          height: heroImage?.height || content.image?.height || '320px',
          scaletype: heroImage?.scaletype || content.image?.scaletype || 'fit',
        },
        imageFit: heroImage?.scaletype || content.image?.scaletype || content.imageFit || 'fit',
        buttonText: content.buttonText || heroFromJson?.button?.label || 'Book Wash',
        buttonNavigation:
          content.buttonNavigation ||
          heroFromJson?.button?.link ||
          heroFromJson?.button?.action ||
          'express-washes',
        backgroundColor:
          content.backgroundColor || heroFromJson?.layout?.background_color || '#1B5BFF',
        layout:
          this.templatePopulator.extractLayoutFromContent(content) ||
          this.templatePopulator.convertLayoutForHeroPanel(
            heroFromJson?.layout?.type || heroFromJson?.layout_type
          ) ||
          'layout_1',
      };

      this.logger.debug('🟡 Final heroPanelData:', ctx.heroPanelData);

      // CRITICAL: Sync currentJsonData with the resolved heading/paragraph values
      // This ensures that even if API returned empty values, we use the resolved values
      if (ctx.heroPanelData.heading || ctx.heroPanelData.paragraph) {
        if (!ctx.json) ctx.json = {};
        if (!ctx.json.page) ctx.json.page = {};
        if (!ctx.json.page.sections) ctx.json.page.sections = {};
        if (!ctx.json.page.sections.hero_section) {
          ctx.json.page.sections.hero_section = {};
        }
        const heroSection = ctx.json.page.sections.hero_section;
        if (!heroSection.title || typeof heroSection.title !== 'object') {
          heroSection.title = {};
        }
        // Only update if the current value is empty but heroPanelData has content
        if (!heroSection.title.label && ctx.heroPanelData.heading) {
          heroSection.title.label = ctx.heroPanelData.heading;
          this.logger.debug(
            '🟡 SYNCED currentJsonData hero title from heroPanelData:',
            ctx.heroPanelData.heading
          );
        }
        if (!heroSection.description && ctx.heroPanelData.paragraph) {
          heroSection.description = ctx.heroPanelData.paragraph;
          this.logger.debug(
            '🟡 SYNCED currentJsonData hero description from heroPanelData:',
            ctx.heroPanelData.paragraph
          );
        }
      }
    } else if (
      element.type === 'section' ||
      element.type === 'services' ||
      element.id === 'welcome' ||
      (element.id && element.id.startsWith('welcome_'))
    ) {
      // Info/Welcome section → Info Panel
      ctx.closeAllPanels();
      ctx.showInfoPanel = true;

      // Determine the section key from element ID
      // Element IDs: 'welcome' for info_section, 'welcome_info_section_1' for info_section_1, etc.
      if (element.id === 'welcome') {
        ctx.currentInfoSectionKey = 'info_section';
      } else if (element.id === 'services' || element.type === 'services') {
        ctx.currentInfoSectionKey = 'info_section_1';
      } else if (element.id && element.id.startsWith('welcome_')) {
        // Extract section key from element ID (e.g., 'welcome_info_section_1' -> 'info_section_1')
        ctx.currentInfoSectionKey = element.id.replace('welcome_', '');
      } else {
        // Fallback: try to find from originalKey if available
        const originalKey = (element as any).originalKey;
        if (originalKey) {
          ctx.currentInfoSectionKey = originalKey;
        } else {
          ctx.currentInfoSectionKey = 'info_section';
        }
      }

      this.logger.debug('📝 Opening info panel for section:', ctx.currentInfoSectionKey);

      const infoElementId = element.id === 'welcome' ? 'welcome' : element.id;
      const infoElement = ctx.elements.find(
        e =>
          (e.type === 'section' || e.type === 'services') &&
          (e.id === infoElementId || e.id === `welcome_${ctx.currentInfoSectionKey}`)
      );
      const content: any = infoElement?.content || element.content || {};
      const infoFromJson = ctx.json?.page?.sections?.[ctx.currentInfoSectionKey];
      const infoImage = infoFromJson?.image || content.image;
      const imageFit =
        content.imageFit || infoImage?.scaletype || content.image?.scaletype || 'fit';
      const imageUrl = content.imageUrl || infoImage?.src || content.image?.src || '';
      const titleStyle =
        content.titleStyle ||
        this.templatePopulator.extractTitleStyleFromApi(infoFromJson?.title?.style) ||
        undefined;
      const layoutFromJson = infoFromJson?.layout?.type || infoFromJson?.layout_type;
      const layoutValue =
        content.layout || (layoutFromJson ? String(layoutFromJson).replace('layout_', '') : '1');

      ctx.infoPanelData = {
        title:
          this.templatePopulator.extractTitleText(content.title) ||
          this.templatePopulator.extractTitleText(infoFromJson?.title) ||
          '',
        description: content.description || infoFromJson?.description || '',
        subtitle: content.subtitle || infoFromJson?.subtitle || '',
        titleStyle,
        imageUrl,
        imageFit,
        image: {
          src: imageUrl,
          alt: infoImage?.alt || content.image?.alt || '',
          width: infoImage?.width || content.image?.width || '420px',
          height: infoImage?.height || content.image?.height || '320px',
          scaletype: imageFit,
        },
        buttonText: content.buttonText || infoFromJson?.button?.label || 'Explore Washes',
        buttonNavigation:
          content.buttonNavigation || infoFromJson?.button?.link || 'express-washes',
        backgroundColor: content.backgroundColor || infoFromJson?.layout?.background_color || '',
        layout: layoutValue,
      };
    } else if (
      element.type === 'packages' ||
      element.id === 'packages' ||
      (element as any).originalKey === 'package_section'
    ) {
      // Package section → Wash Packages Panel
      ctx.closeAllPanels();
      ctx.showWashPackagesPanel = true;
      // Load package data from API and populate panel
      ctx.loadWashPackagesData();
    } else if (
      element.type === 'express-details' ||
      element.id === 'express-details' ||
      (element as any).originalKey === 'hero_section'
    ) {
      // Express Details hero section → Express Details Panel
      ctx.closeAllPanels();
      ctx.showExpressDetailsPanel = true;
      // Load express details data from API and populate panel
      ctx.loadExpressDetailsData();
    } else if (
      element.type === 'coupons' ||
      element.id === 'coupons' ||
      (element as any).originalKey === 'hero_section' ||
      (element as any).originalKey === 'coupon_card' ||
      (element as any).originalKey === 'instructions_section'
    ) {
      // Coupons section → Coupons Panel
      ctx.closeAllPanels();
      ctx.showCouponsPanel = true;
      // Load coupons data from API and populate panel
      ctx.loadCouponsData();
    } else if (
      element.type === 'fleets' ||
      element.id === 'fleets' ||
      (element as any).originalKey === 'hero_section' ||
      (element as any).originalKey === 'form_section'
    ) {
      // Fleets section → Fleets Panel
      ctx.closeAllPanels();
      ctx.showFleetsPanel = true;
      // Load fleets data from API and populate panel
      ctx.loadFleetsData();
    } else if (element.type === 'testimonials') {
      ctx.closeAllPanels();
      ctx.showTestimonialsPanel = true;

      const testimonialsElement = ctx.elements.find(
        e => e.id === 'testimonials' || e.type === 'testimonials'
      );
      const content: any = testimonialsElement?.content || element.content || {};
      const testimonialsSection = ctx.json?.page?.sections?.testimonials_section;
      const rawTestimonials =
        (content.testimonials?.length ? content.testimonials : testimonialsSection?.testimonials) ||
        [];
      const titleStyle =
        content.titleStyle ||
        this.templatePopulator.extractTitleStyleFromApi(testimonialsSection?.title?.style) ||
        undefined;

      ctx.testimonialsData = {
        title:
          this.templatePopulator.extractTitleText(content.title) ||
          this.templatePopulator.extractTitleText(testimonialsSection?.title) ||
          '',
        subtitle:
          content.description ||
          content.subtitle ||
          testimonialsSection?.description ||
          testimonialsSection?.subtitle ||
          '',
        titleStyle,
        testimonials: rawTestimonials.map((t: any) => ({
          id: t.id || t.name || '',
          name: t?.author?.name || t.name || '',
          text: t?.text || t.review || '',
          avatar: t?.author?.avatar || t.avatar || '',
          rating: t?.rating || 5,
        })),
        cardStyle: content.cardStyle || 'default',
        cardColor: content.cardColor || testimonialsSection?.layout?.card_color || '#FFFFFF',
        backgroundColor:
          content.backgroundColor || testimonialsSection?.layout?.background_color || '#FFFFFF',
      };
    } else if (element.type === 'booking') {
      ctx.closeAllPanels();
      ctx.showBookingPanel = true;

      const bookingElement = ctx.elements.find(e => e.id === 'booking' || e.type === 'booking');
      const content: any = bookingElement?.content || element.content || {};
      const bookingSection = ctx.json?.page?.sections?.booking_section;
      const titleStyle =
        content.titleStyle ||
        this.templatePopulator.extractTitleStyleFromApi(bookingSection?.title?.style) ||
        undefined;
      const buttonFromJson = bookingSection?.button;
      const buttonFromContent = content.button;

      ctx.bookingData = {
        title:
          this.templatePopulator.extractTitleText(content.title) ||
          this.templatePopulator.extractTitleText(bookingSection?.title) ||
          '',
        subtitle: content.description || content.subtitle || bookingSection?.subtitle || '',
        titleStyle,
        formTheme: content.formTheme || {
          labelColor: '#555963',
          inputBorderColor: '#E9EAEC',
          inputBackgroundColor: '#FFFFFF',
          inputTextColor: '#15172E',
        },
        button:
          buttonFromContent ||
          (buttonFromJson
            ? {
                text: buttonFromJson.label || buttonFromJson.text || 'Book Appointment',
                backgroundColor:
                  buttonFromJson.style?.background ||
                  buttonFromJson.style?.background_color ||
                  '#1B5BFF',
                textColor:
                  buttonFromJson.style?.color || buttonFromJson.style?.text_color || '#FFFFFF',
              }
            : {
                text: 'Book Appointment',
                backgroundColor: '#1B5BFF',
                textColor: '#FFFFFF',
              }),
        backgroundColor:
          bookingSection?.layout?.background_color || content.backgroundColor || '#FFFFFF',
      };
    } else if (element.type === 'footer' || element.id === 'footer') {
      // Check footer FIRST before contact to prevent footer clicks from opening contact panel
      // Only open footer panel if not loading (prevents auto-opening during initialization)
      if (!ctx.loading) {
        ctx.closeAllPanels();
        ctx.showFooterPanel = true;
      }

      // Populate footer data from element or API - prioritize website-level footer from API
      // 1. website.json_data.website.footer (from API response) - highest priority
      // 2. currentJsonData (from current page)
      // 3. templateData.json_data (from template)
      const websiteFooter = ctx.website?.json_data?.website?.footer;
      const footerSection =
        websiteFooter ||
        ctx.json?.page?.sections?.footer ||
        ctx.templateData?.json_data?.page?.sections?.footer ||
        ctx.templateData?.json_data?.footer ||
        ctx.json?.footer ||
        ctx.templateData?.json_data?.website?.footer;
      const content: any = element.content || footerSection || {};

      // Generate quick links from footer data (API format with column_1 and column_2)
      let quickLinks: any[] = [];
      if (footerSection?.quick_links) {
        const quickLinksObj = footerSection.quick_links;
        if (quickLinksObj.column_1 && Array.isArray(quickLinksObj.column_1)) {
          quickLinks = quickLinks.concat(
            quickLinksObj.column_1.map((link: any) => ({
              label: link.label || '',
              link: link.link || '#',
            }))
          );
        }
        if (quickLinksObj.column_2 && Array.isArray(quickLinksObj.column_2)) {
          quickLinks = quickLinks.concat(
            quickLinksObj.column_2.map((link: any) => ({
              label: link.label || '',
              link: link.link || '#',
            }))
          );
        }
      }
      // Fallback to direct quickLinks array
      if ((!quickLinks || quickLinks.length === 0) && (content.quickLinks || content.quick_links)) {
        quickLinks = Array.isArray(content.quickLinks)
          ? content.quickLinks
          : Array.isArray(content.quick_links)
            ? content.quick_links
            : [];
      }
      // Generate from pages if still empty
      if ((!quickLinks || quickLinks.length === 0) && ctx.pages && ctx.pages.length > 0) {
        const activePages = ctx.pages.filter((page: any) => page.visible !== false);
        quickLinks = activePages.map((page: any) => ({
          label: page.name,
          link: `#${page.pageType || page.name.toLowerCase().replace(/\s+/g, '-')}`,
        }));
      }
      quickLinks = resolveFooterQuickLinks(quickLinks, ctx.pages);

      // Map social links from API format (social_links.platforms)
      let socialLinks: any[] = [];
      if (
        footerSection?.social_links?.platforms &&
        Array.isArray(footerSection.social_links.platforms)
      ) {
        socialLinks = footerSection.social_links.platforms.map((social: any) => ({
          platform: social.platform || '',
          url: social.url || social.link || '#',
          icon: social.icon || social.platform?.toLowerCase() || 'link',
        }));
      }
      // Fallback to direct socialLinks array
      else if (content.socialLinks || content.social_links) {
        const socialArray = Array.isArray(content.socialLinks)
          ? content.socialLinks
          : Array.isArray(content.social_links)
            ? content.social_links
            : [];
        socialLinks = socialArray.map((social: any) => ({
          platform: social.platform || '',
          url: social.url || social.link || '#',
          icon: social.icon || social.platform?.toLowerCase() || 'link',
        }));
      }
      // Use existing footerData socialLinks as last resort
      if ((!socialLinks || socialLinks.length === 0) && ctx.footerData.socialLinks) {
        socialLinks = ctx.footerData.socialLinks;
      }

      // Get background color - prioritize API data (website.json_data.website.footer.theme.background_color)
      const backgroundColor =
        websiteFooter?.theme?.background_color ||
        footerSection?.theme?.background_color ||
        element.content?.backgroundColor ||
        content?.backgroundColor ||
        ctx.footerData?.backgroundColor ||
        '#000920';

      // Get other footer data
      const logo = content.logo || element.content?.logo || ctx.footerData.logo || { text: 'way' };
      const copyright =
        footerSection?.copyright?.text ||
        (typeof footerSection?.copyright === 'string' ? footerSection.copyright : null) ||
        content.copyright ||
        element.content?.copyright ||
        ctx.footerData.copyright ||
        `Copyright © ${new Date().getFullYear()} way.com. All rights reserved.`;
      const quickLinksTitle =
        footerSection?.quick_links?.title ||
        content.quickLinksTitle ||
        element.content?.quickLinksTitle ||
        ctx.footerData.quickLinksTitle ||
        'Quick links';
      const socialLinksTitle =
        footerSection?.social_links?.title ||
        content.socialLinksTitle ||
        element.content?.socialLinksTitle ||
        ctx.footerData.socialLinksTitle ||
        'Connect with us';

      ctx.footerData = {
        logo: logo,
        quickLinks: quickLinks,
        quickLinksTitle: quickLinksTitle,
        socialLinks: socialLinks.length > 0 ? socialLinks : ctx.footerData.socialLinks,
        socialLinksTitle: socialLinksTitle,
        copyright: copyright,
        backgroundColor: backgroundColor,
      };
    } else if (element.type === 'contact' && element.id !== 'footer') {
      // Only open contact panel if it's actually a contact element (not footer)
      this.logger.debug('Opening contact panel for element:', {
        id: element.id,
        type: element.type,
      });
      ctx.closeAllPanels();
      ctx.showContactPanel = true;

      const contactSection = ctx.json?.page?.sections?.contact_section;
      const contactElement = ctx.elements.find(e => e.id === 'contact' || e.type === 'contact');
      const content: any = contactElement?.content || element.content || {};
      const templateName = (ctx.templateData?.name || '').toLowerCase();
      const isDarkTheme = templateName.includes('dark');

      const titleText =
        this.extractContactTitle(contactSection?.title) ||
        this.extractContactTitle(content.title) ||
        'Contact Us';

      const addressStr = this.extractContactString(
        contactSection?.details?.location?.value ||
          contactSection?.details?.address ||
          content.address ||
          content.contactInfo?.address
      );

      const phone = this.extractContactString(
        contactSection?.details?.phone ||
          contactSection?.details?.contact ||
          content.phone ||
          content.contactInfo?.phone
      );

      const email = this.extractContactString(
        contactSection?.details?.email || content.email || content.contactInfo?.email
      );

      const mapEmbedUrl =
        contactSection?.map_embed_url || content.mapEmbedUrl || content.map?.embedLink || '';
      const socialLinks = contactSection?.social_links || content.socialLinks || [];

      let businessHours = mapContactBusinessHours({
        ...contactSection,
        businessHours: contactSection?.businessHours || content.businessHours,
      });

      const mappedSocialLinks = (socialLinks || []).map((social: any) => ({
        platform: social.platform || '',
        url: social.link || social.url || '',
        icon: social.icon || '',
        label: social.label || social.platform || '',
      }));

      if (businessHours.length === 0) {
        businessHours = [
          { day: 'Monday - Friday', openTime: '8:00 AM - 6:00 PM', closed: false },
          { day: 'Saturday', openTime: '8:00 AM - 5:00 PM', closed: false },
          { day: 'Sunday', openTime: '9:00 AM - 4:00 PM', closed: false },
        ];
      }

      let addressValue: any = addressStr;
      if (
        !addressStr &&
        content.contactInfo?.address &&
        typeof content.contactInfo.address === 'object'
      ) {
        addressValue = content.contactInfo.address;
      }

      const defaultBackground = isDarkTheme ? '#0D0E0F' : '#FFFFFF';
      const backgroundColor =
        contactSection?.layout?.background_color ||
        contactSection?.backgroundColor ||
        content.backgroundColor ||
        defaultBackground;

      const defaultLayout = isDarkTheme ? 'layout_2' : 'layout_1';
      const layoutType = contactSection?.layout?.type || content.layout || defaultLayout;

      const titleStyleFromJson =
        contactSection?.title?.style?.text_style || contactSection?.title?.style;
      const titleStyleObj = content.titleStyle || titleStyleFromJson;
      const titleStyle =
        titleStyleObj && typeof titleStyleObj === 'object' && !titleStyleObj.text_style
          ? {
              fontWeight: titleStyleObj.fontWeight || null,
              fontStyle: titleStyleObj.fontStyle || null,
              textDecoration: titleStyleObj.textDecoration || null,
              textAlign: titleStyleObj.textAlign || null,
            }
          : titleStyleObj?.text_style
            ? {
                fontWeight: titleStyleObj.text_style.fontWeight || null,
                fontStyle: titleStyleObj.text_style.fontStyle || null,
                textDecoration: titleStyleObj.text_style.textDecoration || null,
                textAlign: titleStyleObj.text_style.textAlign || null,
              }
            : null;

      let subtitle = contactSection?.subtitle || content.description || content.subtitle || '';
      if (subtitle && addressStr && subtitle === addressStr) {
        subtitle = '';
      }

      ctx.contactData = {
        title: titleText,
        subtitle,
        titleStyle: titleStyle || undefined,
        map: {
          embedLink: mapEmbedUrl || content.map?.embedLink || '',
          imageUrl: content.map?.imageUrl || content.map?.image || '',
          coordinates: content.map?.coordinates || { lat: 0, lng: 0 },
        },
        contactInfo: {
          phone,
          email,
          address: addressValue,
          website: content.contactInfo?.website || '',
        },
        socialLinks: mappedSocialLinks,
        businessHours: businessHours.map(hour => ({
          day: hour.day || '',
          openTime: hour.openTime,
          closeTime: hour.openTime,
          closed: hour.closed,
        })),
        backgroundColor,
        layout: layoutType as 'layout_1' | 'layout_2' | '1' | '2',
      };
    }
  }

  /**
   * Builds and synchronizes hero panel data from the selected element so the correct editor panel opens with populated values.
   * @example
   * etEditorDataFromElement(element, ctx)
   * void
   * @param {any} element - The selected element whose content is used to populate hero panel data.
   * @param {ElementContext} ctx - The current element context containing existing hero panel data and related defaults.
   * @returns {void} Does not return a value.
   **/
  setEditorDataFromElement(element: any, ctx: ElementContext): void {
    // Build hero panel data from selected block so the correct panel opens
    const content: any = element?.content || {};
    const imageSrc =
      content.image?.src ||
      content.imageUrl ||
      content.image?.image ||
      ctx.heroPanelData.image?.src ||
      '';
    const imageScaletype = content.image?.scaletype || ctx.heroPanelData.image?.scaletype || 'fit';
    const nextData: HeroPanelData = {
      heading: content.title || '',
      paragraph: content.description || '',
      image: {
        src: imageSrc,
        alt: content.image?.alt || ctx.heroPanelData.image?.alt || '',
        width: content.image?.width || ctx.heroPanelData.image?.width || '420px',
        height: content.image?.height || ctx.heroPanelData.image?.height || '320px',
        scaletype: imageScaletype,
      },
      // Backward compatibility
      imageUrl: imageSrc,
      imageFit: imageScaletype,
      buttonText: content.buttonText || content.ctaButton?.text || '',
      buttonNavigation: (ctx.heroPanelData.buttonNavigation || 'homepage') as any,
      backgroundColor: ctx.heroPanelData.backgroundColor || '',
      layout:
        this.templatePopulator.extractLayoutFromContent(content) ||
        ctx.heroPanelData.layout ||
        'layout_1',
    };
    ctx.heroPanelData = nextData;
  }

  /** Extract a display string from contact title fields (string or { label, text }). */
  private extractContactTitle(title: any): string {
    if (!title) return '';
    if (typeof title === 'string') return title;
    if (typeof title === 'object') {
      return title.label || title.text || '';
    }
    return String(title);
  }

  /** Extract a display string from contact fields that may be string or { value, label }. */
  private extractContactString(value: any): string {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if (value.street || value.city || value.state || value.zipCode) {
        return [value.street, [value.city, value.state].filter(Boolean).join(', '), value.zipCode]
          .filter(Boolean)
          .join(' ')
          .trim();
      }
      return value.value || value.phone || value.email || value.label || '';
    }
    return String(value);
  }

  private buildHeaderCtaButton(ctaButton: any = {}): {
    text: string;
    link: string;
    buttonNavigation: PanelButtonNavigation;
    backgroundColor: string;
    textColor: string;
  } {
    const navigation = ctaButton.buttonNavigation ?? linkToButtonNavigation(ctaButton.link);
    return {
      text: ctaButton.text || ctaButton.label || 'Book Wash',
      link: buttonNavigationToHashLink(navigation),
      buttonNavigation: navigation,
      backgroundColor: ctaButton.backgroundColor || '#1B5BFF',
      textColor: ctaButton.textColor || '#FFFFFF',
    };
  }

  // Hero Panel Methods
}
